import { z } from "zod";
import { ProjectScriptIcon } from "./project-scripts.ts";
import { AgentEvent } from "./agent-events.ts";
import { ProviderSelection, ProviderStatus } from "./providers.ts";
import { DirectTransportDescriptor, LoopbackTransportDescriptor } from "./transport-descriptor.ts";

/** REST/DB-facing domain models shared by api, web, cli and future native apps. */

export const OrgRole = z.enum(["owner", "member"]);
export type OrgRole = z.infer<typeof OrgRole>;

export const User = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  image: z.string().nullish(),
  avatarUrls: z.array(z.string()).optional(),
  twoFactorEnabled: z.boolean().nullish(),
});
export type User = z.infer<typeof User>;

/** Human-facing Git authorship. Authentication is deliberately separate and
 * continues to use the repository's configured GitHub App credentials. */
export const GitIdentity = z.object({
  name: z.string().trim().min(1).max(200),
  email: z.string().trim().email().max(320),
});
export type GitIdentity = z.infer<typeof GitIdentity>;

/** Immutable attribution for one accepted user turn. `turnId` is issued by
 * Rivets before dispatch; provider-native turn ids are diagnostic only. */
export const TurnActor = z.object({
  turnId: z.string().min(1),
  userId: z.string().min(1),
  gitIdentity: GitIdentity,
});
export type TurnActor = z.infer<typeof TurnActor>;

/** `suspended` is read-only, not a lockout: members still sign in and read, but
 * every write is refused server-side. Clients use it to render a banner and
 * disable their write affordances. */
export const OrgStatus = z.enum(["active", "suspended"]);
export type OrgStatus = z.infer<typeof OrgStatus>;

export const PullRequestMergeMethod = z.enum(["merge", "squash", "rebase"]);
export type PullRequestMergeMethod = z.infer<typeof PullRequestMergeMethod>;

export const Org = z.object({
  image: z.string().nullish(),
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  seatCount: z.number(),
  status: OrgStatus,
  mergeMethod: PullRequestMergeMethod,
  autoArchiveMergedPullRequests: z.boolean(),
  /** Work that arrives from outside the organization — a Linear issue, a
   * Slack message — waits for a member to approve its first run. Agents run
   * with permission prompts disabled on members' machines, and that text is
   * often written by customers or synced from public trackers. Owners may
   * turn this off; it is on for every new organization. */
  requireIntegrationApproval: z.boolean(),
  createdAt: z.number(),
});
export type Org = z.infer<typeof Org>;

export const OrgMember = z.object({
  image: z.string().nullish(),
  avatarUrls: z.array(z.string()).optional(),
  userId: z.string(),
  orgId: z.string(),
  role: OrgRole,
  name: z.string(),
  email: z.string(),
  joinedAt: z.number(),
});
export type OrgMember = z.infer<typeof OrgMember>;

export const Invite = z.object({
  id: z.string(),
  orgId: z.string(),
  email: z.string(),
  token: z.string().optional(),
  status: z.enum(["pending", "accepted", "revoked"]),
  createdAt: z.number(),
});
export type Invite = z.infer<typeof Invite>;

export const License = z.object({
  id: z.string(),
  seats: z.number(),
  status: z.enum(["unclaimed", "active"]),
  purchaseEmail: z.string(),
  createdAt: z.number(),
});
export type License = z.infer<typeof License>;

/** How workers authenticate git operations for a repo. "github-app" repos get
 * short-lived installation tokens minted by the backend; "none" repos must be
 * reachable without credentials (public https, file://) — the CLI disables
 * git credential helpers entirely, so local credentials are never consulted. */
export const RepoAuth = z.enum(["github-app", "none"]);
export type RepoAuth = z.infer<typeof RepoAuth>;

/** A repository icon, normalized by the API to one small image and stored
 * under the content address in `id`. Clients fetch it from the organization's
 * authenticated repo-icon route, which serves it immutably — the id changes
 * whenever the image does, so the bytes never need revalidating.
 *
 * `mediaType` is `image/webp` for everything the API could rasterize and
 * `image/svg+xml` for the one format it cannot; a client that cannot draw SVG
 * shows its monogram instead. */
export const RepoIcon = z.object({
  id: z.string(),
  mediaType: z.string(),
});
export type RepoIcon = z.infer<typeof RepoIcon>;

export const Repo = z.object({
  id: z.string(),
  orgId: z.string(),
  name: z.string(),
  gitUrl: z.string(),
  defaultBranch: z.string(),
  auth: RepoAuth.default("none"),
  githubRepoId: z.number().nullish(),
  githubFullName: z.string().nullish(),
  icon: RepoIcon.nullish(),
  /** Null means this repository inherits its organization's merge method. */
  mergeMethod: PullRequestMergeMethod.nullable().default(null),
  createdAt: z.number(),
});
export type Repo = z.infer<typeof Repo>;

export const GitHubInstallation = z.object({
  installationId: z.number(),
  accountLogin: z.string(),
  status: z.enum(["active", "suspended", "deleted"]),
  createdAt: z.number(),
});
export type GitHubInstallation = z.infer<typeof GitHubInstallation>;

export const WorkerStatus = z.enum(["online", "offline"]);
export type WorkerStatus = z.infer<typeof WorkerStatus>;

/** Lifecycle of one script run on one worktree. `blocked` is the state a
 * setup script puts a job in while the agent waits for it. `stalled` is a
 * setup or archive script still running that has written no output for 2
 * minutes (often a prompt nobody can answer): still running in every
 * respect — Stop applies — and shown as such so the wait is not a mystery. */
export const WorktreeScriptState = z.enum([
  "idle", "running", "stalled", "succeeded", "failed", "timeout", "canceled", "unavailable",
]);

/** Whether a script's process is alive (`running`, or `stalled` in it). */
export function isScriptRunning(state: WorktreeScriptState): boolean {
  return state === "running" || state === "stalled";
}
export type WorktreeScriptState = z.infer<typeof WorktreeScriptState>;

/** One script as the clients see it: what the repo declared, plus where its
 * output is and how the last run ended. */
export const WorktreeScript = z.object({
  /** `setup` and `archive` for the lifecycle hooks; otherwise the run key. */
  id: z.string().min(1).max(48),
  kind: z.enum(["setup", "run", "archive"]),
  name: z.string().min(1).max(80),
  icon: ProjectScriptIcon.default("play"),
  /** Pre-selected in the Run control. Only ever set on one `run` script. */
  isDefault: z.boolean().default(false),
  /** Declared `hide = true`: runnable by id, kept out of the Run menu. Still
   * sent, because a hidden script that has run still owns an output tab. */
  hidden: z.boolean().default(false),
  state: WorktreeScriptState.default("idle"),
  /** The worker-owned terminal carrying this script's output. Clients attach
   * to it like any other terminal; it exists from the first run onward. */
  terminalId: z.string().optional(),
  /** That terminal's current incarnation.
   *
   * A client's terminal announces this with `terminal.created`; a script's
   * never does, because the worker created it. Without it the server would
   * record the terminal at incarnation 0 while the worker is on 1, and the
   * first credit acknowledgement — which is compared against the recorded
   * incarnation — would be refused, leaving the tab drawn but empty. A re-run
   * increments it, which is also how the server learns to revive the row. */
  terminalIncarnation: z.number().int().nonnegative().optional(),
  startedAt: z.number().optional(),
  endedAt: z.number().optional(),
  exitCode: z.number().int().optional(),
  /** Short, redacted reason for `failed`, `timeout`, or `unavailable`. */
  detail: z.string().max(500).optional(),
});
export type WorktreeScript = z.infer<typeof WorktreeScript>;

/** Everything a client needs to draw the Run menu and the script tabs for one
 * worktree. Absent entirely when the worker has not read the repo file yet. */
export const WorktreeScriptSet = z.object({
  worktreeId: z.string(),
  scripts: z.array(WorktreeScript).max(32),
  /** Parse problems from `.rivets/settings.toml`, shown once in the UI so a
   * typo in a shared file is visible rather than silently dropping a script. */
  problems: z.array(z.string().max(300)).max(20).default([]),
});
export type WorktreeScriptSet = z.infer<typeof WorktreeScriptSet>;

export const WorkerCapabilities = z.object({
  /** Worker runs the daemon credential-helper broker; github-app repo jobs
   * are only dispatched to workers that advertise this, so jobs never fall
   * back to ambient local credentials. */
  gitCredentialHelper: z.boolean(),
  /** Maximum provider runtimes this daemon will execute concurrently. The
   * router uses this as a load signal. */
  maxConcurrentJobs: z.number().int().positive().max(1_000),
  /** Worker reads `.rivets/settings.toml`, runs setup on provisioning, and
   * accepts `script.run`. False when its operator turned scripts off, so
   * clients hide the Run control rather than offer a button that does nothing. */
  projectScripts: z.boolean(),
  /** Worker accepts `file.write`, the file editor's save. Saving proves the
   * file is inside the worktree, which only macOS and Linux let a daemon ask,
   * so elsewhere this is false and clients show files read-only rather than
   * let someone type edits that can never be saved. */
  fileWrite: z.boolean(),
  /** Optional, best-effort data path. The relay remains authoritative and is
   * always available; clients must ignore this descriptor unless `state` is
   * `running` and the requested feature is explicitly advertised. */
  directTransport: DirectTransportDescriptor.optional(),
  /** The same gateway over plain loopback, for a client on the worker's own
   * machine. Clients use it only when the advertised `hostId` matches the one
   * on their disk (or, on the web, when the socket actually opens). */
  loopbackTransport: LoopbackTransportDescriptor.optional(),
  providers: z.array(ProviderStatus).max(20),
});
export type WorkerCapabilities = z.infer<typeof WorkerCapabilities>;

/** WorkerHub cannot apply the worker's next durable frame, so everything that
 * worker sends after it — agent output, job status, command receipts — is held
 * behind it. Cleared as soon as the delivery sequence advances. */
export const WorkerDeliveryStall = z.object({
  /** Outbound delivery sequence WorkerHub is waiting to accept. */
  seq: z.number().int().positive(),
  /** Epoch ms of the first failure recorded for this sequence. */
  since: z.number(),
  /** Short, redacted, operator-facing cause. */
  reason: z.string().max(500),
});
export type WorkerDeliveryStall = z.infer<typeof WorkerDeliveryStall>;

/** Who, besides its owner, may run work on a worker: everyone in the
 * organization, the members of one team, or one member. A worker executes
 * agents, terminals and scripts as its OS account, so this is an execution
 * grant; reading tasks, transcripts and diffs still follows repository access. */
export const WorkerAccessGrant = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("everyone") }).strict(),
  z.object({ kind: z.literal("team"), teamId: z.string().min(1).max(100) }).strict(),
  z.object({ kind: z.literal("user"), userId: z.string().min(1).max(100) }).strict(),
]);
export type WorkerAccessGrant = z.infer<typeof WorkerAccessGrant>;

export const WorkerInfo = z.object({
  id: z.string(),
  orgId: z.string(),
  name: z.string(),
  /** Workers with the same cluster id are interchangeable routing targets. */
  clusterId: z.string(),
  clusterName: z.string(),
  status: WorkerStatus,
  capabilities: WorkerCapabilities.nullish(),
  lastSeenAt: z.number().nullish(),
  createdAt: z.number(),
  /** Present while WorkerHub is stuck on this worker's next durable frame. */
  deliveryStall: WorkerDeliveryStall.nullish(),
  /** The member who registered it (or received it by transfer). Only the
   * owner changes `access` or hands the worker to someone else. */
  ownerUserId: z.string(),
  /** Grants beyond the owner. Empty means owner-only. */
  access: z.array(WorkerAccessGrant),
  /** Per viewer: whether the member reading this may run work here — tasks,
   * messages, terminals, scripts, file edits and git actions. */
  canUse: z.boolean(),
});
export type WorkerInfo = z.infer<typeof WorkerInfo>;

/** `awaiting_approval`: integration-sourced work nobody has approved yet. The
 * row exists so the task is visible, but no worker has been asked to create
 * its checkout — terminals, scripts, files and git actions are unavailable
 * until it is approved (then `provisioning`) or rejected (then `archived`). */
export const WorktreeStatus = z.enum(["awaiting_approval", "provisioning", "ready", "error", "archived"]);
export type WorktreeStatus = z.infer<typeof WorktreeStatus>;

/** Provenance for tasks created outside a Rivets client. `kind` deliberately
 * stays open-ended so Slack, Teams and future adapters do not change the task
 * contract; `"mcp"` and `"assistant"` need no contract change either. */
export const TaskSource = z.object({
  kind: z.string().min(1).max(40),
  label: z.string().min(1).max(160),
  url: z.string().url().optional(),
});
export type TaskSource = z.infer<typeof TaskSource>;

/**
 * `source.kind` of the assistant's private scratch workspace.
 *
 * Two different things share the word "assistant" and conflating them is a
 * shipped bug in both directions: `assistant` is the *provenance* of an
 * ordinary task the assistant delegated (visible everywhere), while
 * `assistant-workspace` is the member's own hidden scratch workspace. The
 * latter is per-user and must never reach another member's task list, sidebar
 * or command palette.
 */
export const ASSISTANT_WORKSPACE_SOURCE_KIND = "assistant-workspace";

/** The one predicate every client and the API filter realtime worktrees with,
 * so web, native and the server can never disagree about what is hidden. */
export function isAssistantWorkspace(source: TaskSource | null | undefined): boolean {
  return source?.kind === ASSISTANT_WORKSPACE_SOURCE_KIND;
}

/** Line counts for a set of file changes. */
export const DiffStat = z.object({
  files: z.number().int().nonnegative(),
  additions: z.number().int().nonnegative(),
  deletions: z.number().int().nonnegative(),
});
export type DiffStat = z.infer<typeof DiffStat>;

export const Worktree = z.object({
  id: z.string(),
  repoId: z.string(),
  orgId: z.string(),
  /** The worker the task runs on. Null while it waits for approval, and for a
   * request that was rejected before it ran: integration-sourced work is not
   * placed on any worker until a member approves it (lib/task-approval.ts). */
  workerId: z.string().nullable(),
  name: z.string(),
  branch: z.string(),
  baseBranch: z.string(),
  sourceWorktreeId: z.string().nullish(),
  source: TaskSource.nullish(),
  status: WorktreeStatus,
  /** Set while archived. The owning worker permanently purges the local
   * checkout after the seven-day recovery window. */
  archivedAt: z.number().nullish(),
  createdBy: z.string(),
  createdAt: z.number(),
  /** Totals of the latest snapshot: `branchTotals ?? totals`, never summed
   * (see `diffStatFromSummary`). Lets the sidebar, Home and overview draw
   * +/- counts without fetching a snapshot. All zeros before the first
   * snapshot. */
  diffStat: DiffStat,
  /** The latest snapshot's id, updated together with `diffStat`; null before
   * the first snapshot. */
  snapshotId: z.string().nullable(),
});
export type Worktree = z.infer<typeof Worktree>;

export const PullRequestInfo = z.object({
  number: z.number().int().positive(),
  url: z.string().url(),
  state: z.string(),
  createdBy: z.string(),
  createdAt: z.number(),
});
export type PullRequestInfo = z.infer<typeof PullRequestInfo>;

/** `awaiting_approval`: the chat's first turn came from an integration and
 * has not been approved. It has no job, and messages to it are refused until
 * a member approves or rejects it (`TaskApproval`). */
export const ChatStatus = z.enum(["awaiting_approval", "idle", "queued", "running", "failed", "archived"]);
export type ChatStatus = z.infer<typeof ChatStatus>;

export const Chat = z.object({
  id: z.string(),
  worktreeId: z.string(),
  orgId: z.string(),
  title: z.string(),
  provider: ProviderSelection,
  sourceChatId: z.string().nullish(),
  status: ChatStatus,
  createdBy: z.string(),
  createdAt: z.number(),
  lastEventAt: z.number().nullish(),
  /** The highest event seq the server's log holds for this chat; 0 before the
   * first event. Current at once after a status event (so on the
   * `chat.updated` that ends a turn) and within the flush interval
   * otherwise. A subscribed client holding less than this once the frames in
   * flight have landed is missing the tail, and resubscribes from its cursor. */
  lastSeq: z.number().int().nonnegative(),
  /** Durable prompt authors, independent of who is currently viewing the chat. */
  participantIds: z.array(z.string()).optional(),
});
export type Chat = z.infer<typeof Chat>;

export const TaskApprovalKind = z.enum(["task", "assistant"]);
export type TaskApprovalKind = z.infer<typeof TaskApprovalKind>;

export const TaskApprovalStatus = z.enum(["pending", "approved", "rejected"]);
export type TaskApprovalStatus = z.infer<typeof TaskApprovalStatus>;

/**
 * The human gate in front of integration-sourced work (an organization's
 * `requireIntegrationApproval`). One per chat whose first turn came from
 * outside Rivets: `task` is a whole task a Linear issue started, `assistant`
 * an assistant conversation a channel message started. Until it is approved
 * nothing reaches a worker: no job, no command, no checkout.
 *
 * The approved run executes as the approver — their worker access and their
 * commit identity — and this record keeps who asked and who allowed it.
 */
export const TaskApproval = z.object({
  id: z.string(),
  orgId: z.string(),
  worktreeId: z.string(),
  chatId: z.string(),
  kind: TaskApprovalKind,
  status: TaskApprovalStatus,
  /** Exactly what the agent will be told, external text included. */
  prompt: z.string(),
  /** Where it came from: `kind` is the integration (`linear`, `slack`), `url`
   * the issue or thread when there is one. */
  source: TaskSource,
  /** The member the integration attributed the request to. */
  requestedBy: z.string(),
  requestedByName: z.string(),
  requestedAt: z.number(),
  /** Null while pending, and on a rejection Rivets made itself (the issue was
   * unassigned or stopped in Linear before anyone decided). */
  decidedBy: z.string().nullable(),
  decidedByName: z.string().nullable(),
  decidedAt: z.number().nullable(),
  /** Per viewer: may the member reading this approve it? Approving needs what
   * starting the task yourself needs — write access to the repository and a
   * worker you may use — or, for an assistant conversation, being its owner. */
  canApprove: z.boolean(),
  /** Why not, in words a client shows beside the disabled button. */
  cannotApproveReason: z.string().nullable(),
  /** Rejecting runs nothing, so it needs repository write access (task) or
   * ownership (assistant) but no worker. */
  canReject: z.boolean(),
});
export type TaskApproval = z.infer<typeof TaskApproval>;

export const JobStatus = z.enum([
  "queued",
  "assigned",
  "running",
  "completed",
  "failed",
  "canceled",
]);
export type JobStatus = z.infer<typeof JobStatus>;

export const Job = z.object({
  id: z.string(),
  chatId: z.string(),
  workerId: z.string(),
  orgId: z.string(),
  status: JobStatus,
  provider: ProviderSelection,
  agentSessionId: z.string().nullish(),
  error: z.string().nullish(),
  createdAt: z.number(),
  finishedAt: z.number().nullish(),
});
export type Job = z.infer<typeof Job>;

export const FileChangeStatus = z.enum(["added", "modified", "deleted", "renamed", "untracked"]);
export type FileChangeStatus = z.infer<typeof FileChangeStatus>;

export const FileChange = z.object({
  path: z.string(),
  oldPath: z.string().optional(),
  status: FileChangeStatus,
  additions: z.number(),
  deletions: z.number(),
  /** True when the index has unresolved merge stages for this path. */
  conflicted: z.boolean().optional(),
  /** Unified diff hunks for this file (absent for binary files). */
  diff: z.string().optional(),
  /** UTF-8 byte length of the diff before any cap was applied. Set by workers
   * that apply the per-file cap or the aggregate budget, so a summary can
   * report the original size; absent means `diff` is the whole diff. */
  diffBytes: z.number().int().nonnegative().optional(),
  /** The diff was capped, or dropped (`diff` absent) by the aggregate
   * per-snapshot budget. The stats above stay complete either way. */
  diffTruncated: z.boolean().optional(),
});
export type FileChange = z.infer<typeof FileChange>;

export const GitCommit = z.object({
  body: z.string().max(4000).optional(),
  sha: z.string().min(1),
  subject: z.string(),
  authorName: z.string(),
  // Git permits intentionally overridden/non-RFC author strings. Normal
  // Rivets commits still use the validated account email from TurnActor.
  authorEmail: z.string(),
  authoredAt: z.number(),
});
export type GitCommit = z.infer<typeof GitCommit>;

export const Snapshot = z.object({
  id: z.string(),
  worktreeId: z.string(),
  branch: z.string(),
  ahead: z.number(),
  behind: z.number(),
  /** Tracked and untracked paths in the worktree, used to resolve file links in
   * the transcript. Not the file browser's source: that reads a directory at a
   * time from the worker, so it sees ignored files too. */
  paths: z.array(z.string()),
  /** Uncommitted index and working-tree changes. */
  changes: z.array(FileChange),
  /** All changes on the task branch relative to its base, including `changes`.
   * Every upload carries it; optional because snapshot blobs already stored in
   * R2 without it cannot be rewritten by a migration. */
  branchChanges: z.array(FileChange).optional(),
  /** Task-branch commits since its base, including their real Git authors. */
  commits: z.array(GitCommit),
  createdAt: z.number(),
});
export type Snapshot = z.infer<typeof Snapshot>;

/** The per-file diff cap workers apply before upload (characters). */
export const SNAPSHOT_FILE_DIFF_MAX_CHARS = 200_000;
/** Aggregate diff-text budget per snapshot upload. Past it, files keep full
 * stats with `diffTruncated: true` and no `diff`. */
export const SNAPSHOT_DIFF_BUDGET_BYTES = 8 * 1024 * 1024;

/** `diffHash`: lowercase sha-256 hex of the served diff text, or `""` when
 * there is no diff text. */
const DiffHash = z.string().regex(/^(?:[0-9a-f]{64})?$/, "diffHash must be sha-256 hex or empty");

/** A `FileChange` without its diff text. Diffs are fetched per file on demand
 * (`SnapshotDiffResponse`), keyed by `(snapshotId, path, scope)`. */
export const SnapshotFileSummary = FileChange.omit({ diff: true, diffBytes: true, diffTruncated: true }).extend({
  /** Original UTF-8 byte length of the diff, before any cap. 0 when none. */
  diffBytes: z.number().int().nonnegative(),
  /** sha-256 hex of the diff text the diff endpoint serves (possibly capped),
   * or `""` when there is none. */
  diffHash: DiffHash,
  diffTruncated: z.boolean().optional(),
});
export type SnapshotFileSummary = z.infer<typeof SnapshotFileSummary>;

/** A snapshot with no diff text and no path list. Computed once at upload and
 * stored separately, so serving it never parses the full snapshot blob.
 * LocalApi serves the same shape with an `lsnap_` id. */
export const SnapshotSummary = z.object({
  id: z.string(),
  worktreeId: z.string(),
  branch: z.string(),
  ahead: z.number(),
  behind: z.number(),
  changes: z.array(SnapshotFileSummary),
  branchChanges: z.array(SnapshotFileSummary).optional(),
  commits: z.array(GitCommit),
  createdAt: z.number(),
  /** Totals over `changes`. */
  totals: DiffStat,
  /** Totals over `branchChanges`, when present. */
  branchTotals: DiffStat.optional(),
  /** Length of the full snapshot's `paths`; the list itself is served by
   * `GET /api/snapshots/:id/paths`. */
  pathsCount: z.number().int().nonnegative(),
});
export type SnapshotSummary = z.infer<typeof SnapshotSummary>;

/** Image formats that provider CLIs can receive as native image input. Other
 * attachment types are still accepted: the worker saves them in `.context`
 * and tells the agent where to read them. */
export const ATTACHMENT_IMAGE_MEDIA_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"] as const;
/** A bounded, syntactically valid MIME type. `application/octet-stream` is the
 * fallback for files whose client cannot identify a more specific type. */
export const AttachmentMediaType = z.string().trim().toLowerCase().max(127)
  .regex(/^[a-z0-9!#$&^_.+-]+\/[a-z0-9!#$&^_.+-]+$/, "Invalid attachment media type");
export type AttachmentMediaType = z.infer<typeof AttachmentMediaType>;

export function isAttachmentImageMediaType(mediaType: string): boolean {
  return (ATTACHMENT_IMAGE_MEDIA_TYPES as readonly string[]).includes(mediaType.toLowerCase());
}

/** Per-message attachment count. Bounds the ChatEvent payload, the JobSpec
 * frame, and the number of files a worker materializes for one turn. */
export const MAX_ATTACHMENTS_PER_MESSAGE = 8;
/** Per-file byte ceiling, enforced on upload (declared length and actual bytes). */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENT_NAME_LENGTH = 200;

/** Pointer to an uploaded file. Only the id is authoritative — the server
 * reloads the stored record and never trusts client-supplied metadata. */
export const AttachmentRef = z.object({
  id: z.string(),
  mediaType: AttachmentMediaType,
  size: z.number().int().positive().max(MAX_ATTACHMENT_BYTES),
  name: z.string().max(MAX_ATTACHMENT_NAME_LENGTH).optional(),
  /** Intrinsic pixel dimensions, when the uploading client measured them. */
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
});
export type AttachmentRef = z.infer<typeof AttachmentRef>;

/** The one bounded attachment list shape used by REST, events and WS frames. */
export const AttachmentRefs = z.array(AttachmentRef).max(MAX_ATTACHMENTS_PER_MESSAGE);
export type AttachmentRefs = z.infer<typeof AttachmentRefs>;

/** Longest team message, in characters. Team chat is conversation between
 * people, not a prompt, so it is far shorter than `SendMessageRequest.text`. */
export const TEAM_MESSAGE_MAX_CHARS = 4_000;
/** Most members one team message may mention. */
export const TEAM_MESSAGE_MAX_MENTIONS = 20;
/** Most team messages one prompt may carry to the agent as context. */
export const TEAM_CONTEXT_MAX_MESSAGES = 50;

/** Team messages a prompt carried to the agent, in the order they were said.
 * The words themselves stay in the team messages: the `user.message` holds
 * only what its author typed, so editing it cannot rewrite what others said. */
export const TeamMessageContext = z.object({
  messageIds: z.array(z.string().min(1).max(100)).min(1).max(TEAM_CONTEXT_MAX_MESSAGES),
});
export type TeamMessageContext = z.infer<typeof TeamMessageContext>;

/** Members a team message mentions, by user id. Clients highlight `@Name` for
 * each of them and the server notifies them. Mentioning grants no access. */
export const TeamMentions = z.array(z.string().min(1).max(100)).max(TEAM_MESSAGE_MAX_MENTIONS);

/**
 * One entry in a member's inbox, kept per recipient, so reading one is that
 * member's business, and read on any device for every device.
 *
 * - `mention`: a teammate named them in a team message.
 * - `run.finished`: a run in a task they follow completed or failed.
 * - `prompt`: someone else directed the agent in a task they follow.
 * - `discussion`: people talked to the team in a task they follow, without
 *   mentioning them.
 *
 * `prompt` and `discussion` gather into one unread entry per chat (`count`),
 * so a lively chat is one line in the inbox rather than one per message.
 */
export const InboxNotificationKind = z.enum(["mention", "run.finished", "prompt", "discussion"]);
export type InboxNotificationKind = z.infer<typeof InboxNotificationKind>;

export const InboxNotification = z.object({
  id: z.string(),
  kind: InboxNotificationKind,
  orgId: z.string(),
  /** Who caused it; absent once that account is gone. */
  actorId: z.string().optional(),
  worktreeId: z.string(),
  chatId: z.string(),
  /** The team message a mention came from. */
  teamMessageId: z.string().optional(),
  /** Names as they were when the inbox was read, for the row's headline. */
  taskName: z.string(),
  chatTitle: z.string(),
  /** The start of what was said, as last edited; for a run, its error. */
  excerpt: z.string(),
  /** How a finished run ended. */
  jobStatus: z.enum(["completed", "failed"]).optional(),
  /** How many prompts or messages a gathered entry stands for. */
  count: z.number().int().positive().default(1),
  /** When it last changed: a gathered entry moves up as it grows. */
  createdAt: z.number(),
  readAt: z.number().nullable(),
});
export type InboxNotification = z.infer<typeof InboxNotification>;

/** Longest excerpt an inbox entry keeps. */
export const INBOX_EXCERPT_MAX_CHARS = 280;

/** One row in a chat's append-only, seq-ordered event log. */
export const ChatEventPayload = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("user.message"),
    text: z.string(),
    clientRequestId: z.string().min(1).max(64).optional(),
    attachments: AttachmentRefs.optional(),
    /** Set when the prompt carried part of the team discussion to the agent. */
    teamContext: TeamMessageContext.optional(),
  }),
  /**
   * People talking to each other in the chat, between and during the agent's
   * turns. Never delivered to the worker or the agent: only a prompt that
   * names it in `teamContext` passes its words on. Attributed by the event's
   * `userId` and part of no turn, so it carries no `turnId`. `messageId` is
   * the handle edits and deletions name.
   */
  z.object({
    kind: z.literal("team.message"),
    messageId: z.string().min(1).max(100),
    text: z.string(),
    mentions: TeamMentions.default([]),
  }),
  /** Its author rewrote a team message; replaces its text and mentions. */
  z.object({
    kind: z.literal("team.message.revised"),
    messageId: z.string().min(1).max(100),
    text: z.string(),
    mentions: TeamMentions.default([]),
  }),
  /** Its author deleted a team message. Clients stop showing it. */
  z.object({ kind: z.literal("team.message.withdrawn"), messageId: z.string().min(1).max(100) }),
  z.object({
    kind: z.literal("handoff.context"),
    sourceChatId: z.string(),
    sourceProviderId: z.string(),
    importedEventCount: z.number().int().nonnegative(),
    transcriptTruncated: z.boolean(),
    transferredText: z.string().max(32_000),
  }),
  z.object({ kind: z.literal("agent"), event: AgentEvent }),
  z.object({
    kind: z.literal("job.status"),
    status: JobStatus,
    workerId: z.string().optional(),
    error: z.string().optional(),
  }),
]);
export type ChatEventPayload = z.infer<typeof ChatEventPayload>;

/** The inclusive seq interval a history read *scanned*, including events it
 * elided and seqs that do not exist. A client advances its contiguity cursor
 * to `max(cursor, throughSeq)` and never re-requests seqs below it. */
export const ChatEventsCoverage = z.object({
  fromSeq: z.number().int().nonnegative(),
  throughSeq: z.number().int().nonnegative(),
}).refine((coverage) => coverage.fromSeq <= coverage.throughSeq, "coverage.fromSeq must not exceed throughSeq");
export type ChatEventsCoverage = z.infer<typeof ChatEventsCoverage>;

/** `ChatEvent` without its turn-attribution refinement, for schemas derived
 * from it (`ProvisionalChatEvent`). Parse chat events with `ChatEvent`. */
export const ChatEventBase = z.object({
  seq: z.number(),
  chatId: z.string(),
  ts: z.number(),
  /** Attribution: required for user messages and agent output; absent only for system events. */
  userId: z.string().optional(),
  /** Present on user messages and all agent output belonging to that turn. */
  turnId: z.string().optional(),
  jobId: z.string().optional(),
  /** Stable identity (`chat_event.event_id`): `deliveryEventId(...)` for
   * worker output, `${commandId}:…` for command projections. Absent on rows
   * written before event ids existed. Clients preserve it; it is what promotes
   * a provisional event. */
  eventId: z.string().max(300).optional(),
  payload: ChatEventPayload,
});

/** Shared by `ChatEvent` and `ProvisionalChatEvent`. */
export function requireTurnAttribution(
  event: { userId?: string; turnId?: string; payload: ChatEventPayload },
  ctx: z.RefinementCtx,
): void {
  if ((event.payload.kind === "user.message" || event.payload.kind === "agent") && (!event.userId || !event.turnId)) {
    ctx.addIssue({ code: "custom", message: "turn-scoped chat events require userId and turnId" });
  }
  if (isTeamMessagePayload(event.payload) && !event.userId) {
    ctx.addIssue({ code: "custom", message: "team messages require userId" });
  }
}

/** True for the team-chat kinds: people talking to each other, which the
 * agent never receives and which belong to no turn. */
export function isTeamMessagePayload(payload: ChatEventPayload): payload is Extract<ChatEventPayload, { kind: "team.message" | "team.message.revised" | "team.message.withdrawn" }> {
  return payload.kind === "team.message" || payload.kind === "team.message.revised" || payload.kind === "team.message.withdrawn";
}

export const ChatEvent = ChatEventBase.superRefine(requireTurnAttribution);
export type ChatEvent = z.infer<typeof ChatEvent>;
